import type { ParticipantDocument, ParticipantFile } from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, FancyDate, FileIcon, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaEye, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { LabelsStack } from '../../components/generic/LabelsStack';
import { ListItem } from '../../components/generic/ListItem';
import { showModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { TextLink } from '../../components/generic/TextLink';
import {
  useParticipantDocumentsQuery,
  useParticipantFilesQuery,
} from '../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { isParticipantFile, type ParticipantFileOrDocument } from '../../utils/fileOrDocument';
import { ParticipantFileActionMenu } from '../files/ParticipantFileActionMenu';
import { ParticipantFileUploadModal } from '../files/ParticipantFileUploadModal';
import { ParticipantDocumentActionMenu } from '../participantDocument/ParticipantDocumentActionMenu';

interface Props {
  participantId: string;
  isDisabled?: boolean;
}

export const ParticipantDocumentsCard = (props: Props) => {
  const { participantId, isDisabled } = props;

  const { organization } = useAppContext();
  const navigate = useNavigate();

  const onViewAllFiles = () => {
    void navigate(`/organizations/${organization.id}/participants/${participantId}/documents`);
  };

  const onAddFile = () => {
    showModal(ParticipantFileUploadModal, {
      participantId,
    });
  };

  const documentsQuery = useParticipantDocumentsQuery({
    organizationId: organization.id,
    participantId,
  });

  const filesQuery = useParticipantFilesQuery({
    organizationId: organization.id,
    participantId,
  });

  const isLoading = filesQuery.isLoading || documentsQuery.isLoading;
  const error = filesQuery.error || documentsQuery.error;

  const files = (filesQuery.data?.organization?.participant?.files as ParticipantFile[]) ?? [];
  const documents =
    (documentsQuery.data?.organization?.participant?.documents as ParticipantDocument[]) ?? [];
  const combined = P.orderBy([...files, ...documents], (f) => f.createdAt, 'desc');
  const recentFileOrDocuments: ParticipantFileOrDocument[] = P.take(combined, 3);

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError h='full' error={error} />;
    }

    if (P.isEmpty(combined)) {
      return <Empty h='full' title='No documents found' p={0} />;
    }

    return recentFileOrDocuments.map((fileOrDocument) => {
      const { to, isExternal } = isParticipantFile(fileOrDocument)
        ? { to: fileOrDocument.viewUrl, isExternal: true }
        : {
            to: `/organizations/${organization.id}/participants/${fileOrDocument.participantId}/documents/${fileOrDocument.id}`,
            isExternal: false,
          };

      return (
        <ListItem
          key={fileOrDocument.id}
          icon={<FileIcon name={fileOrDocument.name} w={10} h={10} />}
          title={fileOrDocument.name}
          secondary={
            <FancyDate
              date={fileOrDocument.createdAt}
              showHumanized={false}
              color='secondary'
              fontSize='xs'
            />
          }
          tertiary={<LabelsStack labels={fileOrDocument.labels} />}
          right={
            <ChakraV3.HStack>
              <ChakraV3.Button size='sm' variant='plain' w='fit-content'>
                <TextLink to={to} isExternal={isExternal} color='secondary'>
                  View
                </TextLink>
              </ChakraV3.Button>
              {isParticipantFile(fileOrDocument) ? (
                <ParticipantFileActionMenu file={fileOrDocument} size='sm' disabled={isDisabled} />
              ) : (
                <ParticipantDocumentActionMenu
                  participantDocument={fileOrDocument}
                  size='sm'
                  disabled={isDisabled}
                  color='secondary'
                />
              )}
            </ChakraV3.HStack>
          }
        />
      );
    });
  });

  return (
    <ChakraV3.Card.Root h='full'>
      <ChakraV3.Card.Header>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Heading size='xl' fontWeight='bold'>
            Documents
          </ChakraV3.Heading>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Recent documents
          </ChakraV3.Text>
        </ChakraV3.Stack>
      </ChakraV3.Card.Header>
      <ChakraV3.Card.Body as={ChakraV3.Stack} gap={6}>
        {content}
        <ChakraV3.HStack w='full'>
          <ChakraV3.Button onClick={onViewAllFiles} flex={1} size='sm' variant='subtle'>
            <FaEye />
            View all documents
          </ChakraV3.Button>
          <ChakraV3.Button
            onClick={onAddFile}
            flex={1}
            size='sm'
            disabled={isDisabled}
            variant='subtle'
          >
            <FaPlus />
            Upload
          </ChakraV3.Button>
        </ChakraV3.HStack>
      </ChakraV3.Card.Body>
    </ChakraV3.Card.Root>
  );
};
